import { FileExcelOutlined } from '@ant-design/icons';
import { CommerceConf, IssueTaskStatus, OrderStatusCode, ProductSupplierDoc } from '@gooduncles/gu-app-schema';
import { Button, Tooltip, message, notification } from 'antd';
import { uniq } from 'lodash-es';
import { FC, useCallback, useState } from 'react';

import { calculateDeliveryTaskFinishedAt, formatDate } from 'src/utils/date-util';
import { getIssueTasks, getStoreIssues } from 'src/utils/firebase/firebase-short-cut';
import { AuditLogger } from 'src/utils/audit-logger';
import {
  OrderWithStore,
  downloadOrderProductsToExcel,
  issueTaskToRawData,
  storeIssueToRawData,
} from 'src/utils/order-util';

const auditLogger = AuditLogger.getInstance();

type RawDataDownloadButtonProps = {
  orders: OrderWithStore[];
  selectedStatus: number[];
  commerceConf: CommerceConf | null;
  productSuppliers: ProductSupplierDoc[] | undefined;
  disabled: boolean;
};

const RawDataDownloadButton: FC<RawDataDownloadButtonProps> = ({
  orders,
  selectedStatus,
  commerceConf,
  productSuppliers,
  disabled,
}) => {
  const [loading, setLoading] = useState(false);
  const downloadRawDataExcel = useCallback(async () => {
    if (!productSuppliers) {
      message.error('거래처를 가져오지 못했습니다. 개발자에게 알려주세요.');
      return;
    }

    if (!commerceConf) {
      message.error('이웃삼촌 설정을 가져오지 못했습니다. 개발자에게 알려주세요.');
      return;
    }

    // 배송 완료 주문의 관련 이슈를 가져오기 위함.
    const deliveryTaskFinishedAtList = uniq(
      orders
        .filter((order) => order.deliveryTaskFinishedAt)
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        .map((order) => formatDate(order.deliveryTaskFinishedAt!, "yyyy-MM-dd'T'00:00:00+0900")),
    );
    const notFinishedOrders = orders.filter((order) =>
      [OrderStatusCode.ACCEPTED, OrderStatusCode.SUBMITTED].includes(order.orderStatus),
    );
    const willBeDeliveredAtList = uniq(notFinishedOrders.map((order) => formatDate(order.orderDate, 'yyyy-MM-dd'))).map(
      (d) => {
        const deliveryTaskFinishedAt = calculateDeliveryTaskFinishedAt(
          commerceConf.deliveryDateRules,
          "yyyy-MM-dd'T'00:00:00+0900",
          new Date(d),
        );
        return deliveryTaskFinishedAt;
      },
    );

    setLoading(true);
    try {
      const issueDates = [...deliveryTaskFinishedAtList, ...willBeDeliveredAtList];
      const issues = ((await getStoreIssues([['date', 'in', issueDates]])) ?? [])
        .filter((issue) => issue.isDeleted !== true)
        .filter((issue) => ['완료', '입력'].includes(issue.status ?? ''));
      const issueRawData = storeIssueToRawData(issues);

      // IssueTask 조회를 위한 날짜 범위 계산
      const sortedDates = issueDates.sort();
      const startValueWithTime = sortedDates[0]; // 가장 이른 날짜 (이미 00:00:00 형식)
      const endValueWithTime = sortedDates[sortedDates.length - 1].replace('T00:00:00', 'T23:59:59'); // 가장 늦은 날짜의 23:59:59

      // IssueTask도 동일한 날짜 범위에서 조회
      const issueTasks = (
        (await getIssueTasks([], {
          sortKey: 'finishedAt',
          orderBy: 'desc',
          startValue: startValueWithTime,
          endValue: endValueWithTime,
        })) ?? []
      ).filter((task) => task.status === IssueTaskStatus.COMPLETED);
      const issueTaskRawData = await issueTaskToRawData(issueTasks);
      // issueRawData와 issueTaskRawData를 병합
      const mergedIssueRawData = [...issueRawData, ...issueTaskRawData];

      const rawDataLength = await downloadOrderProductsToExcel(
        orders,
        mergedIssueRawData,
        {
          statusList: selectedStatus,
          deliveryDateRules: commerceConf.deliveryDateRules,
        },
        productSuppliers,
      );
      auditLogger.log({
        action: 'SYNC',
        entity: 'order',
        entityId: 'excel-export',
        summary: `주문 관리 - 주문내역[${selectedStatus.join(', ')}] ${rawDataLength}개 다운로드 완료`,
      });
    } catch (error) {
      console.error(error);
      if (error instanceof TypeError) {
        const reasons = error.cause as string[];
        notification.error({
          message: error.message,
          description: `오류 원인 목록: ${reasons.join(', ')}\n사용자 관리에서 식당코드를 입력해주세요.`,
          style: { whiteSpace: 'pre-line' },
          duration: 0,
        });
      } else {
        console.error(error);
        auditLogger.log({
          action: 'SYNC',
          entity: 'order',
          entityId: 'excel-export',
          summary: `주문 관리 - 주문내역[${selectedStatus.join(', ')}] 다운로드 과정에서 에러 발생.`,
          level: 'error',
        });
        message.error('주문내역을 다운로드 과정에서 알 수 없는 에러가 발생했습니다. 개발자에게 알려주세요.');
      }
    }
    setLoading(false);
  }, [commerceConf, orders, selectedStatus, productSuppliers]);

  return (
    <Tooltip title='한 번에 내려받을 수 있는 기간은 최대 7일입니다.'>
      <Button icon={<FileExcelOutlined />} onClick={downloadRawDataExcel} loading={loading} disabled={disabled}>
        raw data 내려받기
      </Button>
    </Tooltip>
  );
};

export default RawDataDownloadButton;
